import { useMemo, useEffect } from 'react';
import {
  getIsNativeApp,
  getIsWebApp,
  MOBILE_TOUCH_GESTURES_MEDIA_QUERY,
} from '../utils/appShellEnvironment';
import { useMatchMedia } from './useMatchMedia';

export { MOBILE_TOUCH_GESTURES_MEDIA_QUERY } from '../utils/appShellEnvironment';

interface AppEnvironmentResult {
  isNativeApp: boolean;
  isWebApp: boolean;
  isMobileViewport: boolean;
  /** Native, PWA, or mobile-width browser — use for swipe / drawer gestures. */
  supportsTouchGestures: boolean;
  shouldApplySafeZones: boolean;
}

/**
 * Detect native Capacitor shell, installed PWA, and mobile viewport.
 * Manages body classes for CSS safe zones when applyBodyClasses is true.
 */
export const useAppEnvironment = (applyBodyClasses: boolean = true): AppEnvironmentResult => {
  const isNativeApp = useMemo(() => getIsNativeApp(), []);
  const isWebApp = useMemo(() => getIsWebApp(), []);
  const isMobileViewport = useMatchMedia(MOBILE_TOUCH_GESTURES_MEDIA_QUERY);

  const supportsTouchGestures = isNativeApp || isWebApp || isMobileViewport;
  const shouldApplySafeZones = isNativeApp || isWebApp;

  useEffect(() => {
    if (applyBodyClasses) {
      document.body.classList.toggle('is-native-app', isNativeApp);
      document.body.classList.toggle('is-web-app', isWebApp);
    }

    return () => {
      // Don't remove on unmount to avoid flicker if other components are managing them
    };
  }, [applyBodyClasses, isNativeApp, isWebApp]);

  return {
    isNativeApp,
    isWebApp,
    isMobileViewport,
    supportsTouchGestures,
    shouldApplySafeZones,
  };
};

export default useAppEnvironment;
